import exportIconDark from "@/assets/img/icon-export.svg";
import importIconDark from "@/assets/img/icon-import.svg";
import reloadIconBlack from "@/assets/img/icon-reload-black.svg";
import trashIconDark from "@/assets/img/icon-trash.svg";
import plusIconLight from "@/assets/img/plus-icon-thin-light.svg";
import { PaginationStateWrapper } from "@/components/shared/table/PaginationStateWrapper.component";
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import { Loader } from "../Loader.component";
import TableActionButton from "./TableActionButton.component";
import style from "./TableActions.component.module.scss";
import TablePagination from "./TablePagination.component";
import { IPaginationState } from "./types";

const plusIconSrc = plusIconLight;
const trashIconSrc = trashIconDark;
const importIconSrc = importIconDark;
const exportIconSrc = exportIconDark;
const reloadIconSrc = reloadIconBlack;

@Component
export default class TableActions extends Vue {
  _tsx!: tsx.DeclareProps<{
    canDeleteItems?: TableActions["canDeleteItems"];
    canExport?: TableActions["canExport"];
    canAddItems?: TableActions["canAddItems"];
    canImport?: TableActions["canImport"];
    isLoading?: TableActions["isLoading"];
    withImport?: TableActions["withImport"];
    withExport?: TableActions["withExport"];
    withDelete?: TableActions["withDelete"];
    withDivider?: TableActions["withDivider"];
    withReload?: TableActions["withReload"];
    withPagination?: TableActions["withPagination"];
    addActionCopy?: TableActions["addActionCopy"];
    hasMoreItems?: TableActions["hasMoreItems"];
    paginationState?: PaginationStateWrapper["paginationState"];
    totalRows?: TableActions["totalRows"];
  }> &
    tsx.DeclareOnEvents<{
      onAddEntry: void;
      onImport: void;
      onExport: void;
      onReload: void;
      onDeleteEntries: void;
      onPaginationChange: IPaginationState;
    }>;

  @Prop({ default: false }) canDeleteItems: boolean;
  @Prop({ default: false }) canExport: boolean;
  @Prop({ default: false }) canAddItems: boolean;
  @Prop({ default: false }) canImport: boolean;
  @Prop({ default: false }) isLoading: boolean;
  @Prop({ default: false }) withImport: boolean;
  @Prop({ default: false }) withExport: boolean;
  @Prop({ default: false }) withDelete: boolean;
  @Prop({ default: false }) withDivider: boolean;
  @Prop({ default: false }) withReload: boolean;
  @Prop({ default: false }) withPagination: boolean;
  @Prop({ required: false }) addActionCopy: string;
  @Prop({ required: false }) private hasMoreItems: boolean;
  @Prop({ required: false }) paginationState: IPaginationState;
  @Prop({ required: false }) totalRows: number;

  @Emit("addEntry") onAddEntry() {}
  @Emit("deleteEntries") onDeleteEntries() {}
  @Emit("import") onImport() {}
  @Emit("export") onExport() {}
  @Emit("reload") onReload() {}
  @Emit("paginationChange") onPaginationChange(
    _paginationState: IPaginationState,
  ) {}

  public get addEntryButtonRef(): HTMLButtonElement {
    return (this.$refs.addEntryButton as TableActionButton).buttonRef;
  }

  private get shouldShowPagination() {
    return this.hasMoreItems || this.totalRows > this.paginationState?.perPage;
  }

  render() {
    return (
      <div class={style.container}>
        <TableActionButton
          data-testid="addEntry"
          onClick={this.onAddEntry}
          disabled={!this.canAddItems}
          ref="addEntryButton"
          class={style.primaryButton}
        >
          <img src={plusIconSrc} class={style.icon} />
          <span>{this.addActionCopy ?? this.$t("global_cta_new")}</span>
        </TableActionButton>
        {this.withDelete && (
          <TableActionButton
            data-testid="deleteEntries"
            disabled={!this.canDeleteItems}
            onClick={this.onDeleteEntries}
          >
            <img src={trashIconSrc} class={style.icon} />
            <span>{this.$t("global_cta_delete")}</span>
          </TableActionButton>
        )}
        {this.withDivider && <div class={style.divider} />}
        {this.$slots.default}
        {this.withReload && (
          <TableActionButton
            onClick={this.onReload}
            class={style.reloadButton}
            disabled={this.isLoading}
          >
            {this.isLoading ? (
              <Loader small />
            ) : (
              <img src={reloadIconSrc} class={style.icon} />
            )}

            <span class={style.desktopOnly}>
              {this.$t("global_cta_reload")}
            </span>
          </TableActionButton>
        )}
        {this.withDivider && <div class={style.divider} />}
        {this.withImport && (
          <TableActionButton
            onClick={this.onImport}
            class={style.importButton}
            disabled={!this.canImport}
          >
            <img src={importIconSrc} class={style.icon} />
            <span class={style.desktopOnly}>
              {this.$t("global_cta_import")}
            </span>
          </TableActionButton>
        )}
        {this.withExport && (
          <TableActionButton
            onClick={this.onExport}
            class={style.exportButton}
            disabled={!this.canExport}
          >
            <img src={exportIconSrc} class={style.icon} />
            <span class={style.desktopOnly}>
              {this.$t("global_cta_export")}
            </span>
          </TableActionButton>
        )}
        {this.shouldShowPagination && this.withDivider && (
          <div class={style.divider} />
        )}
        {this.shouldShowPagination && (
          <div class={[style.pagination, style.desktopOnly]}>
            <TablePagination
              onUpdate={this.onPaginationChange}
              paginationState={this.paginationState}
              totalRows={this.totalRows}
              hasMore={this.hasMoreItems}
              disablePerPageSelect
            />
          </div>
        )}
      </div>
    );
  }
}
